(() => {
  const root = document.getElementById('pde');
  if (!root) return;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = () => reduced() ? 'auto' : 'smooth';
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  function directionOf(button) {
    const explicit = Number(button.getAttribute('data-dir'));
    if (explicit === -1 || explicit === 1) return explicit;
    const text = `${button.getAttribute('aria-label') || ''}`;
    if (/(?:prev|vorher|zurück)/i.test(text)) return -1;
    if (/(?:next|nächst|weiter)/i.test(text)) return 1;
    return 0;
  }

  function initTrack(track) {
    const items = [...track.children].filter((node) => node.matches('li,article,.pjc-solution__ingredient'));
    if (!items.length) return;
    const buttons = [...root.querySelectorAll('button[aria-controls]')]
      .filter((button) => button.getAttribute('aria-controls') === track.id && directionOf(button));
    const controlBar = buttons[0]?.closest('.carousel-controls,.wpe-ingredient-controls');
    const counter = controlBar?.querySelector('.counter,[aria-live]');
    if (!buttons.length) return;
    let scheduled = false;

    function metrics() {
      const rect = track.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const positions = items.map((item) => Math.max(0,
        item.getBoundingClientRect().left - rect.left + track.scrollLeft - padding));
      return { max, positions };
    }
    function update() {
      scheduled = false;
      const { max, positions } = metrics();
      const overflow = max > 3 && track.clientWidth > 0;
      const current = overflow
        ? positions.reduce((nearest, position, index) =>
            Math.abs(position - track.scrollLeft) < Math.abs(positions[nearest] - track.scrollLeft) ? index : nearest, 0)
        : 0;
      if (controlBar) controlBar.hidden = !overflow;
      track.tabIndex = overflow ? 0 : -1;
      if (overflow) track.setAttribute('aria-roledescription', 'Karussell');
      else track.removeAttribute('aria-roledescription');
      buttons.forEach((button) => {
        const direction = directionOf(button);
        button.disabled = !overflow || (direction < 0 ? track.scrollLeft <= 3 : track.scrollLeft >= max - 3);
      });
      if (counter) counter.textContent = `${current + 1} / ${items.length}`;
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    }
    function go(direction) {
      const { max, positions } = metrics();
      if (max <= 3) return;
      const x = track.scrollLeft;
      const target = direction > 0
        ? (positions.find((position) => position > x + 4) ?? max)
        : ([...positions].reverse().find((position) => position < x - 4) ?? 0);
      track.scrollTo({ left: clamp(target, 0, max), behavior: behavior() });
    }
    buttons.forEach((button) => {
      button.type = 'button';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        go(directionOf(button));
      });
    });
    track.addEventListener('scroll', schedule, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        go(event.key === 'ArrowRight' ? 1 : -1);
      }
    });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(schedule);
      observer.observe(track);
      items.forEach((item) => observer.observe(item));
    }
    update();
  }

  root.querySelectorAll('#wpe-ingredient-track,.track').forEach(initTrack);

  const formula = root.querySelector('.formula-section');
  if (formula) {
    const composition = formula.querySelector('.formula-composition');
    const track = formula.querySelector('.ingredient-track');
    const cards = [...formula.querySelectorAll('.ingredient')];
    const dots = [...formula.querySelectorAll('[data-select]')];
    const mobile = window.matchMedia('(max-width:1000px)');
    let active = composition?.dataset.active || 'collagen';
    let frame;
    function update(id) {
      active = id;
      if (composition) composition.dataset.active = id;
      cards.forEach((card) => card.classList.toggle('is-active', card.dataset.ingredient === id));
      dots.forEach((dot) => {
        const selected = dot.dataset.select === id;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-pressed', String(selected));
      });
      const index = cards.findIndex((card) => card.dataset.ingredient === id);
      const prev = formula.querySelector('[data-direction="-1"]');
      const next = formula.querySelector('[data-direction="1"]');
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === cards.length - 1;
    }
    function select(id) {
      update(id);
      if (!mobile.matches || !track) return;
      const card = cards.find((item) => item.dataset.ingredient === id);
      if (!card) return;
      track.scrollTo({ left: card.offsetLeft - cards[0].offsetLeft, behavior: behavior() });
    }
    dots.forEach((dot) => dot.addEventListener('click', () => select(dot.dataset.select)));
    formula.querySelectorAll('[data-direction]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = cards.findIndex((card) => card.dataset.ingredient === active);
        const next = Math.max(0, Math.min(cards.length - 1, index + Number(button.dataset.direction)));
        select(cards[next].dataset.ingredient);
      });
    });
    track?.addEventListener('scroll', () => {
      if (!mobile.matches) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const distance = (card) => Math.abs(card.offsetLeft - cards[0].offsetLeft - track.scrollLeft);
        const nearest = cards.reduce((a, b) => distance(a) < distance(b) ? a : b);
        update(nearest.dataset.ingredient);
      });
    }, { passive: true });
    update(active);
  }

  root.querySelectorAll('details').forEach((details) => {
    const sync = () => details.setAttribute('aria-expanded', String(details.open));
    details.addEventListener('toggle', sync);
    sync();
  });
  root.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const link = event.target.closest?.('a[href^="#"]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href.length < 2) return;
    const target = document.getElementById(decodeURIComponent(href.slice(1)));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: behavior(), block: 'start' });
  });

  const bar = document.getElementById('pde-stick');
  const offer = root.querySelector('#wpe-s32 .wpe-btn');
  if (bar && offer && 'IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      bar.hidden = entries[0].isIntersecting;
    }, { rootMargin: '0px 0px -25% 0px' }).observe(offer);
  }
})();
